import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'

const typographyVariants = cva('', {
  variants: {
    variant: {
      h1: 'scroll-m-20 text-4xl font-extrabold tracking-tight lg:text-5xl',
      h2: 'scroll-m-20 border-b pb-2 text-3xl font-semibold tracking-tight first:mt-0',
      h3: 'scroll-m-20 text-2xl font-semibold tracking-tight',
      h4: 'scroll-m-20 text-xl font-semibold tracking-tight',
      h5: 'scroll-m-20 text-lg font-semibold tracking-tight',
      h6: 'scroll-m-20 text-base font-semibold tracking-tight',
      p: 'leading-7 [&:not(:first-child)]:mt-6',
      blockquote: 'mt-6 border-l-2 pl-6 italic',
      code: 'relative rounded bg-muted px-[0.3rem] py-[0.2rem] text-sm font-semibold',
      pre: 'overflow-x-auto rounded-lg bg-muted p-4 text-sm',
      lead: 'text-xl text-muted-foreground',
      large: 'text-lg font-semibold',
      small: 'text-sm font-medium leading-none',
      muted: 'text-sm text-muted-foreground',
      ul: 'my-6 ml-6 list-disc [&>li]:mt-2',
      ol: 'my-6 ml-6 list-decimal [&>li]:mt-2',
      li: 'leading-7',
      a: 'font-medium text-primary underline underline-offset-4 hover:text-primary/80',
      table: 'w-full border-collapse overflow-y-auto',
      thead: 'border-b',
      tbody: '[&>tr:last-child]:border-0',
      tr: 'border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted',
      th: 'h-12 px-4 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0',
      td: 'p-4 align-middle [&:has([role=checkbox])]:pr-0',
      kbd: 'pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 text-[10px] font-medium text-muted-foreground opacity-100',
      mark: 'bg-yellow-200 dark:bg-yellow-900 px-1 rounded',
      del: 'line-through decoration-2 decoration-muted-foreground',
      ins: 'underline decoration-2 decoration-green-600 dark:decoration-green-400',
      sub: 'align-sub text-xs',
      sup: 'align-super text-xs',
      hr: 'border-border',
    },
  },
  defaultVariants: {
    variant: 'p',
  },
})

export interface TypographyProps
  extends React.HTMLAttributes<HTMLElement>,
    VariantProps<typeof typographyVariants> {
  as?:
    | 'h1'
    | 'h2'
    | 'h3'
    | 'h4'
    | 'h5'
    | 'h6'
    | 'p'
    | 'span'
    | 'div'
    | 'blockquote'
    | 'code'
    | 'pre'
    | 'ul'
    | 'ol'
    | 'li'
    | 'a'
    | 'table'
    | 'thead'
    | 'tbody'
    | 'tr'
    | 'th'
    | 'td'
    | 'kbd'
    | 'mark'
    | 'del'
    | 'ins'
    | 'sub'
    | 'sup'
    | 'hr'
}

const Typography = React.forwardRef<HTMLElement, TypographyProps>(
  ({ className, variant, as, children, ...props }, ref) => {
    const Comp = as || getDefaultElement(variant) || 'p'
    return React.createElement(
      Comp,
      {
        className: cn(typographyVariants({ variant, className })),
        ref,
        ...props,
      },
      children
    )
  }
)

Typography.displayName = 'Typography'

function getDefaultElement(variant?: string | null): string {
  switch (variant) {
    case 'h1':
      return 'h1'
    case 'h2':
      return 'h2'
    case 'h3':
      return 'h3'
    case 'h4':
      return 'h4'
    case 'h5':
      return 'h5'
    case 'h6':
      return 'h6'
    case 'blockquote':
      return 'blockquote'
    case 'code':
      return 'code'
    case 'pre':
      return 'pre'
    case 'ul':
      return 'ul'
    case 'ol':
      return 'ol'
    case 'li':
      return 'li'
    case 'a':
      return 'a'
    case 'table':
      return 'table'
    case 'thead':
      return 'thead'
    case 'tbody':
      return 'tbody'
    case 'tr':
      return 'tr'
    case 'th':
      return 'th'
    case 'td':
      return 'td'
    case 'kbd':
      return 'kbd'
    case 'mark':
      return 'mark'
    case 'del':
      return 'del'
    case 'ins':
      return 'ins'
    case 'sub':
      return 'sub'
    case 'sup':
      return 'sup'
    case 'hr':
      return 'hr'
    case 'lead':
    case 'large':
    case 'small':
    case 'muted':
    default:
      return 'p'
  }
}

export { Typography, typographyVariants }
